import { useCallback, useEffect, useState } from "react";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import {
  GetApiMethod,
  PostApiMethod,
  PutApiMethod,
  DeleteApiMethod,
} from "../../Utils/ApiService";
import BoxHeader from "../../Components/Box/BoxHeader";
import { AntTab, AntTabs } from "../../Components/Tab/AntTab";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Grid,
  FormLabel,
  TextField,
  FormControlLabel,
  Checkbox,
  Switch,
} from "@mui/material";
import MenuGroup from "../../Components/DropDown/MenuGroup";
import { CustomModal, ModalStyle } from "../../Components/ModalStyle";
import { Form, Formik } from "formik";
import { permissionSchema, roleSchema } from "../HomePages/AddUserSchema";
import { RoleDto, PermissionDto } from "../../Dto/Generic";
import { DisplayErrorMessages } from "../../Utils/HelperService";
import { useAppDispatch } from "../../Store/hooks";
import { showLoader } from "../../Store/loaderSlice";
import { showToaster } from "../../Store/toastSlice";
import { CustomTablePagination } from "../../Components/Pagination/CustomTablePagination";
import TableSearch from "../../Components/Search/TableSearch";

const RolePages = () => {
  const [role, setRole] = useState<RoleDto[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const pages = [
    { buttonName: "Role", buttonShow: true, dashboardName: "Role" },
    {
      buttonName: "Permission",
      buttonShow: false,
      dashboardName: "Permission",
    },
  ];
  const [formModal, setFormModal] = useState(false);
  const [roleData, setRoleData] = useState<RoleDto>({
    id: "",
    roleName: "",
    displayName: "",
    permissions: [],
    ismobile: false,
  });
  const [permissions, setPermissions] = useState<PermissionDto[]>([]);
  const dispatch = useAppDispatch();
  const [permissionDetail, setPermissionDetail] = useState<PermissionDto>({
    id: "",
    permissionName: "",
    displayName: "",
    description: "",
  });
  const [permissionModal, setPermissionModal] = useState(false);
  // Roles
  const [rolePage, setRolePage] = useState(0);
  const [roleRowsPerPage, setRoleRowsPerPage] = useState(10);
  const [roleTotalCount, setRoleTotalCount] = useState(0);
  const [rolePagePermissions, setRolePagePermissions] = useState<
    PermissionDto[]
  >([]);

  // Permissions
  const [permissionPage, setPermissionPage] = useState(0);
  const [permissionRowsPerPage, setPermissionRowsPerPage] = useState(10);
  const [permissionTotalCount, setPermissionTotalCount] = useState(0);

  const getRole = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));

      const params = new URLSearchParams({
        page: (rolePage + 1).toString(),
        limit: roleRowsPerPage.toString(),
        sort: "displayName",
        sortDirection: "DESC",
      });

      if (searchTerm) params.append("search", searchTerm);

      const res = await GetApiMethod(`roles?${params.toString()}`);
      setRole(res.data.data.records);
      setRoleTotalCount(res.data.data.totalCount);
      dispatch(showLoader({ showLoader: false }));
    } catch (error) {
      setRole([]);
      setRoleTotalCount(0);
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, rolePage, roleRowsPerPage, searchTerm]);
  const getPermissions = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));

      const params = new URLSearchParams({
        page: (permissionPage + 1).toString(),
        limit: permissionRowsPerPage.toString(),
        sort: "displayName",
        sortDirection: "DESC",
      });

      if (searchTerm) params.append("search", searchTerm); // <-- Add this

      const res = await GetApiMethod(`permissions?${params.toString()}`);
      setPermissions(res.data.data.records);
      setPermissionTotalCount(res.data.data.totalCount);
      dispatch(showLoader({ showLoader: false }));
    } catch (error) {
      setPermissions([]);
      setPermissionTotalCount(0);
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, permissionPage, permissionRowsPerPage, searchTerm]);

  const getRolePermissions = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));

      const params = new URLSearchParams({
        page: "1",
        limit: "100",
        sort: "displayName",
        sortDirection: "DESC",
      });

      const res = await GetApiMethod(`permissions?${params.toString()}`);
      setRolePagePermissions(res.data.data.records);
      dispatch(showLoader({ showLoader: false }));
    } catch (error) {
      setRolePagePermissions([]);
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, permissionPage, permissionRowsPerPage, searchTerm]);

  const handleTabChange = useCallback(
    (e: React.ChangeEvent<{}>, val: number) => {
      setTabValue(val);
    },
    []
  );

  const resetForm = useCallback(() => {
    setFormModal(false);
    setRoleData({
      id: "",
      roleName: "",
      displayName: "",
      permissions: [],
      ismobile: false,
    });
  }, []);

  const roleSubmit = useCallback(
    async (values: RoleDto) => {
      try {
        dispatch(showLoader({ showLoader: true }));

        const apiMethod = values.id
          ? PutApiMethod("roles/" + values.id, values)
          : PostApiMethod("roles", values);
        await apiMethod;
        resetForm();
        dispatch(showLoader({ showLoader: false }));

        getRole();
      } catch (error) {
        dispatch(showLoader({ showLoader: false }));
        const errorMsg = DisplayErrorMessages(error);

        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: errorMsg,
            showDialog: true,
            timer: "5000",
          })
        );
      }
    },
    [dispatch, getRole, resetForm]
  );

  const getRoleDetail = useCallback(
    async (id: string) => {
      try {
        dispatch(showLoader({ showLoader: true }));

        const res = await GetApiMethod("roles/" + id);
        setRoleData({
          ...res.data.data,
          permissions: res.data.data.Permissions.map(
            (p: PermissionDto) => p.id
          ),
        });
        dispatch(showLoader({ showLoader: false }));
      } catch (error) {
        dispatch(showLoader({ showLoader: false }));
        const errorMsg = DisplayErrorMessages(error);

        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: errorMsg,
            showDialog: true,
            timer: "5000",
          })
        );
      }
    },
    [dispatch]
  );

  useEffect(() => {
    getRolePermissions();
  }, [getRolePermissions]);

  useEffect(() => {
    if (tabValue === 0) {
      getRole();
    } else {
      getPermissions();
    }
  }, [tabValue, getRole, getPermissions, searchTerm]);

  const permissionSubmit = useCallback(
    async (value: PermissionDto) => {
      try {
        dispatch(showLoader({ showLoader: true }));

        await PutApiMethod("permissions/" + value.id, value);
        setPermissionModal(false);
        getPermissions();
      } catch (error) {
        dispatch(showLoader({ showLoader: false }));
        const errorMsg = DisplayErrorMessages(error);

        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: errorMsg,
            showDialog: true,
            timer: "5000",
          })
        );
      }
    },
    [dispatch, getPermissions]
  );
  const deleteRole = useCallback(
    async (id: string) => {
      try {
        dispatch(showLoader({ showLoader: true }));

        await DeleteApiMethod(`roles/${id}`);

        dispatch(showLoader({ showLoader: false }));

        getRole();

        dispatch(
          showToaster({
            dialogBgColor: "bg-success",
            dialogMsg: "Role deleted successfully",
            showDialog: true,
            timer: "3000",
          })
        );
      } catch (error) {
        dispatch(showLoader({ showLoader: false }));

        const errorMsg = DisplayErrorMessages(error);
        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: errorMsg,
            showDialog: true,
            timer: "5000",
          })
        );
      }
    },
    [dispatch, getRole]
  );
  return (
    <AuthorizedLayout>
      <BoxHeader
        dashboard_name="Role Management"
        button_name={"Add " + pages[tabValue].buttonName}
        onAdd={() => {
          setFormModal(true);
          setRoleData({
            id: "",
            roleName: "",
            displayName: "",
            permissions: [],
            ismobile: false,
          });
        }}
        button_show={pages[tabValue].buttonShow}
      >
        <AntTabs value={tabValue} onChange={handleTabChange}>
          <AntTab label="Role" className="tab-menu" />
          <AntTab label="Permission" className="tab-menu" />
        </AntTabs>
      </BoxHeader>
      <TableSearch
        value={searchTerm}
        onChange={(value) => {
          setSearchTerm(value);
          if (tabValue === 0) setRolePage(0);
          else setPermissionPage(0);
        }}
        placeholder={
          tabValue === 0
            ? "Search roles by name..."
            : "Search permissions by name..."
        }
      />
      <TableContainer className="mt-2">
        {tabValue === 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {role.map((val, key) => (
                <TableRow key={key}>
                  <TableCell>{val.displayName}</TableCell>
                  <TableCell>
                    <Switch
                      checked={val.ismobile} // true = Active, false = InActive
                      onChange={(e) => {
                        const newStatus = e.target.checked;
                      }}
                      color="primary"
                      disabled={true}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <MenuGroup
                      id={val.id}
                      view={false}
                      edit={true}
                      delete={true}
                      clickEdit={() => {
                        setFormModal(true);
                        setRoleData({
                          id: val.id,
                          roleName: val.roleName,
                          displayName: val.displayName,
                          permissions: [],
                          ismobile: val.ismobile,
                        });
                        getRoleDetail(val.id);
                      }}
                      clickDelete={() => deleteRole(val.id)}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {role.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No Data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>

                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.map((val, key) => (
                <TableRow key={key}>
                  <TableCell>{val.displayName}</TableCell>
                  <TableCell>{val.description}</TableCell>

                  <TableCell align="right">
                    <MenuGroup
                      id={val.id}
                      view={false}
                      edit={true}
                      delete={false}
                      clickEdit={() => {
                        setPermissionModal(true);
                        setPermissionDetail(val);
                      }}
                      clickDelete={() => {}}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {permissions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No Data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <CustomTablePagination
        count={tabValue === 0 ? roleTotalCount : permissionTotalCount}
        page={tabValue === 0 ? rolePage : permissionPage}
        rowsPerPage={tabValue === 0 ? roleRowsPerPage : permissionRowsPerPage}
        onPageChange={(_, newPage) => {
          if (tabValue === 0) setRolePage(newPage);
          else setPermissionPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const value = parseInt(event.target.value, 10);
          if (tabValue === 0) {
            setRoleRowsPerPage(value);
            setRolePage(0);
          } else {
            setPermissionRowsPerPage(value);
            setPermissionPage(0);
          }
        }}
      />

      <CustomModal
        open={formModal}
        onClose={() => {
          resetForm();
          dispatch(showLoader({ showLoader: false }));
        }}
        header={`${roleData?.id ? "Edit " : "Create "}Role`}
        style={ModalStyle({ width: 600, height: "auto" })}
      >
        <Formik
          initialValues={roleData}
          validationSchema={roleSchema}
          onSubmit={async (values, { resetForm }) => {
            roleSubmit(values);
          }}
          enableReinitialize={true}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            errors,
            touched,
            setFieldValue,
          }) => (
            <>
              <Box className="modal-body">
                <Form onSubmit={handleSubmit}>
                  <Grid item md={12} m={2}>
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold">
                        Name:<sup className="text-danger">*</sup>
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="Type here..."
                        variant="standard"
                        value={values.displayName}
                        name="displayName"
                        onChange={handleChange}
                      />
                      {touched.displayName && errors.displayName && (
                        <div className="text-danger">{errors.displayName}</div>
                      )}
                    </Grid>

                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold">
                        Mobile:<sup className="text-danger">*</sup>
                      </FormLabel>
                      <Switch
                        name="ismobile"
                        checked={values.ismobile} // true = Active, false = InActive
                        onChange={handleChange}
                        color="primary"
                      />
                    </Grid>

                    <Box mt={2} ml={1}>
                      <FormLabel className="fw-bold">Permission:</FormLabel>
                      <Box
                        display="flex"
                        flexDirection="column"
                        ml={2}
                        mt={1}
                        sx={{
                          maxHeight: 300, // adjust height so ~10 items fit
                          overflowY: "auto",
                          // border: "1px solid #ddd",
                          borderRadius: "4px",
                          // padding: "8px",
                          // hide scrollbar
                          scrollbarWidth: "none", // Firefox
                          "&::-webkit-scrollbar": {
                            display: "none", // Chrome, Safari
                          },
                          msOverflowStyle: "none",
                        }}
                      >
                        {rolePagePermissions.map((item, index) => (
                          <FormControlLabel
                            key={index}
                            control={
                              <Checkbox
                                checked={values.permissions.includes(item.id)}
                                onChange={() => {
                                  setFieldValue(
                                    "permissions",
                                    values.permissions.includes(item.id)
                                      ? values.permissions.filter(
                                          (p) => p !== item.id
                                        )
                                      : [...values.permissions, item.id]
                                  );
                                }}
                              />
                            }
                            label={item.displayName}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                  <Box className="d-flex justify-content-end mt-3 p-3">
                    <button className="btn btn-danger" type="submit">
                      Submit
                    </button>
                  </Box>
                </Form>
              </Box>
            </>
          )}
        </Formik>
      </CustomModal>
      <CustomModal
        open={permissionModal}
        onClose={() => {
          dispatch(showLoader({ showLoader: false }));
          resetForm();
        }}
        header={`${permissionDetail?.id ? "Edit " : "Create "}Role`}
        style={ModalStyle({ width: 600, height: "auto" })}
      >
        <Formik
          initialValues={permissionDetail}
          validationSchema={permissionSchema}
          onSubmit={async (values, { resetForm }) => {
            permissionSubmit(values);
          }}
          enableReinitialize={true}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            errors,
            touched,
            setFieldValue,
          }) => (
            <>
              <Box className="modal-body">
                <Form onSubmit={handleSubmit}>
                  <Grid item md={12} m={2}>
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold">
                        Name:<sup className="text-danger">*</sup>
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="Type here..."
                        variant="standard"
                        value={values.displayName}
                        name="displayName"
                        onChange={handleChange}
                      />
                      {touched.displayName && errors.displayName && (
                        <div className="text-danger">{errors.displayName}</div>
                      )}
                    </Grid>
                    <Grid container spacing={1} className="p-3">
                      <FormLabel className="fw-bold">
                        Description:<sup className="text-danger">*</sup>
                      </FormLabel>
                      <TextField
                        className="w-100"
                        placeholder="Type here..."
                        variant="standard"
                        value={values.description}
                        name="description"
                        onChange={handleChange}
                      />
                      {touched.description && errors.description && (
                        <div className="text-danger">{errors.description}</div>
                      )}
                    </Grid>
                  </Grid>
                  <Box className="d-flex justify-content-end mt-3 p-3">
                    <button className="btn btn-danger" type="submit">
                      Submit
                    </button>
                  </Box>
                </Form>
              </Box>
            </>
          )}
        </Formik>
      </CustomModal>
    </AuthorizedLayout>
  );
};

export default RolePages;
